import { type VideoSegment } from '@solinkcorp/playlist-parser';
import classNames from 'classnames';
import { type BufferedSegment } from 'components/VideoPlayer/controller/PlaybackController';
import { type ScaleTime } from 'd3-scale';
import _chunk from 'lodash/chunk';
import React, { useMemo } from 'react';
import styles from './RecordingBar.module.sass';
import TimelineBlock from './TimelineBlock';

// If two segments have a gap between them which is less than MAX_ALLOWED_SEGMENT_GAP,
// we'll consider them contiguous.
export const MAX_ALLOWED_SEGMENT_GAP = 1000;

// Divide up segments data into blocks.  Since we're rendering contiguous segments,
// the amount of time covered by 40 segments will likely be all of the segments,
// but this ensures we're not trying to render a bazillion little boxes if a
// camera keeps cutting in and out.
const SEGMENTS_PER_BLOCK = 40;

// Note that this is compatible with `SegmentData` from VideoUtil.
export interface LoadedVideoSegments {
  readonly segments: readonly VideoSegment[];
  readonly start: number;
  readonly end: number;
}

export type ContiguousSegment = Pick<VideoSegment, 'start' | 'end'>;

interface RecordingBarsProps {
  videoSegments: LoadedVideoSegments | undefined;
  bufferedSegments: readonly BufferedSegment[] | undefined;
  /** Scale to use to render the recording bar. */
  timeScale: ScaleTime<number, number>;
  height: number;
  className?: string;
  transform?: string;
  highlight?: boolean;
}

/**
 * RecordingBar renders a bar into an SVG which represents what recording
 * segments are available.
 *
 * This renders a bar which is dark grey in areas where no video is available,
 * and light grey in areas where video is available.
 *
 * Note that the `videoSegments` passed in here may or may not be the same as
 * the `motionSegments` prop passed into other components in the VideoPlayerFooter
 * - when watching an HD stream, `videoSegments` will be based on the HD stream
 * data, whereas `motionSegments` will be based on the SD stream data, since HD
 * streams don't have motion data.
 */
const RecordingBars: React.FC<RecordingBarsProps> = (props) => {
  const { videoSegments, bufferedSegments, timeScale, highlight } = props;

  const begin = timeScale.domain()[0];
  const end = timeScale.domain()[1];

  const segments = videoSegments?.segments;
  const splitSegments = useMemo(() => {
    const contiguousSegments = getContiguousSegments(segments);
    return _chunk(contiguousSegments, SEGMENTS_PER_BLOCK);
  }, [segments]);

  let missingSegmentsBar: JSX.Element | undefined;
  if (videoSegments) {
    const overallStart = timeScale(videoSegments.start);
    const overallEnd = timeScale(videoSegments.end);

    if (overallEnd < overallStart) {
      console.error(`Invalid segments: ${videoSegments.end} is before ${videoSegments.start}`);
    } else {
      missingSegmentsBar = (
        <rect
          className={`${styles['missing-segment']} missing-segment`}
          x={overallStart}
          width={overallEnd - overallStart}
        />
      );
    }
  }

  // Draw a dark grey box covering the entire `videoSegments` range, then draw
  // light grey boxes on top for the segments.
  // (TODO: The old code would just draw a "domain" across the whole screen as the dark bar...)
  return (
    <g className={props.className} transform={props.transform}>
      {missingSegmentsBar}
      {splitSegments.map((segments) => {
        const blockStart = segments[0].start;
        const blockEnd = segments[segments.length - 1].end;
        const width = Math.max((blockEnd - blockStart) / 1000, 0);

        if (blockEnd < begin.valueOf() || blockStart > end.valueOf()) {
          // This block is off-screen
          return null;
        }

        return (
          <TimelineBlock
            key={`${blockStart}-${blockEnd}`}
            start={blockStart}
            end={blockEnd}
            width={width}
            timeScale={timeScale}
          >
            <MemoSegmentRenderer
              segments={segments}
              height={props.height}
              highlight={!!highlight}
            />
          </TimelineBlock>
        );
      })}
      {bufferedSegments?.map((buffer, index) => {
        const start = timeScale(buffer.start);
        const end = timeScale(buffer.end);

        if (end < start) {
          return null;
        }
        return (
          <rect
            className={`${styles['buffer']} buffer`}
            key={`buffer-${index}`}
            x={start}
            y={0}
            width={end - start}
            height={props.height}
          />
        );
      })}
    </g>
  );
};

export default React.memo(RecordingBars);

interface SegmentRendererProps {
  segments: ContiguousSegment[];
  height: number;
  highlight: boolean;
}

/**
 * Renders an array of ContiguousSegments as white bars.
 */
const SegmentRenderer: React.FC<SegmentRendererProps> = (props) => {
  {
    const { segments, height, highlight } = props;

    // Render all segments starting at x=0.  The wrapping TimelineBlock
    // will position the segments in the correct spot on the timeline.
    const segmentsStart = segments[0].start;
    return (
      <>
        {segments.map((s, index) => {
          if (s.end < s.start) {
            return null;
          }
          return (
            <rect
              className={classNames(styles['segment'], 'segment', {
                [styles.highlight]: highlight,
              })}
              key={index}
              x={(s.start - segmentsStart) / 1000}
              y={0}
              width={(s.end - s.start) / 1000}
              height={height}
            />
          );
        })}
      </>
    );
  }
};

const MemoSegmentRenderer = React.memo(SegmentRenderer);

/**
 * Convert segments into merged contiguous segments.
 * Given an array of segments, this returns an array of `{ start, end }`
 * objects.  If two or more segments are contiguous (or have less than a 1
 * second gap between them) they will be merged into a single block in the
 * output.
 */
export function getContiguousSegments(
  segments?: readonly ContiguousSegment[],
): ContiguousSegment[] {
  if (!segments || segments.length < 1) {
    return [];
  }

  const merged = [];
  let currentStart = segments[0].start;
  let currentEnd = segments[0].end;

  for (let i = 1; i < segments.length; i++) {
    if (segments[i].start - currentEnd > MAX_ALLOWED_SEGMENT_GAP) {
      const block = { start: currentStart, end: currentEnd };
      merged.push(block);
      currentStart = segments[i].start;
      currentEnd = segments[i].end;
    } else {
      currentEnd = Math.max(segments[i].end, currentEnd);
    }
  }

  // close off the final block
  if (merged.length === 0 || merged[merged.length - 1].end !== currentEnd) {
    const block = { start: currentStart, end: currentEnd };
    merged.push(block);
  }

  return merged;
}
